import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'js-cookie';
import FilmDetailRow from './FilmDetailRow';
import SearchableSelect from './SearchableSelect';

const NewCustomReport = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  // STATE DECLARATIONS
  const [customers, setCustomers] = useState([]);
  const [filmBrands, setFilmBrands] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingReport, setFetchingReport] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nbDropdownOpen, setNbDropdownOpen] = useState(false);
  const [filmSizes, setFilmSizes] = useState([]);
  const [observations, setObservations] = useState([]);
  const [filmDetails, setFilmDetails] = useState([]);
  const [addFilmData, setAddFilmData] = useState({
    filmSize: '',
    sequenceType: 'Single Alphabet',
    quantity: '1',
    groups: '1',
    isCs: false
  });
  const [replaceData, setReplaceData] = useState({
    fromSize: '',
    toSize: '',
    findJointId: '',
    replaceJointId: ''
  });
  const [filmSizeSearch, setFilmSizeSearch] = useState('');
  const [addFilmSizeSearch, setAddFilmSizeSearch] = useState('');
  const [replaceFromSearch, setReplaceFromSearch] = useState('');
  const [replaceToSearch, setReplaceToSearch] = useState('');

  // CONSTANTS
  // Remark options
  const remarkOptions = [
    'Repair',
    'Acceptable',
    'Reshoot',
    'DRS',
    'Fill up and DRS',
    'Retake',
    'Fill up',
    'CRS',
    'Check Surface',
    'L I',
    'L II',
    'L III',
    'L IV',
    'L V',
    'L VI',
    'L VII',
    'L VIII',
    'Not Acceptable',
    'Reshoot to Confirm'
  ];

  // NB values mapping
  const nbValues = {
    6: 10.3,
    8: 13.7,
    10: 17.1,
    15: 21.3,
    20: 26.7,
    25: 33.4,
    32: 42.2,
    40: 48.3,
    50: 60.3,
    65: 73.0,
    80: 88.9,
    100: 114.3,
    125: 141.3,
    150: 168.3,
    200: 219.1,
    250: 273.0,
    300: 323.9,
    350: 355.6,
    400: 406.4,
    450: 457.0,
    500: 508.0,
    600: 609.6,
    750: 762.0,
    900: 914.4,
    1050: 1066.8,
    1200: 1219.2
  };

  // Wire selection ranges
  const wireSelectionRanges = [
    { min: 0.25, max: 6.4, sourceSideWire: "5th Wire", filmSideWire: "4th Wire" },
    { min: 6.4, max: 9.5, sourceSideWire: "6th Wire", filmSideWire: "5th Wire" },
    { min: 9.5, max: 12.7, sourceSideWire: "7th Wire", filmSideWire: "6th Wire" },
    { min: 12.7, max: 19.0, sourceSideWire: "8th Wire", filmSideWire: "7th Wire" },
    { min: 19.0, max: 25.4, sourceSideWire: "9th Wire", filmSideWire: "8th Wire" },
    { min: 25.4, max: 38.1, sourceSideWire: "10th Wire", filmSideWire: "9th Wire" },
    { min: 38.1, max: 50.8, sourceSideWire: "11th Wire", filmSideWire: "10th Wire" },
    { min: 50.8, max: 63.5, sourceSideWire: "12th Wire", filmSideWire: "11th Wire" },
    { min: 63.5, max: 101.6, sourceSideWire: "13th Wire", filmSideWire: "12th Wire" },
    { min: 101.6, max: 152.4, sourceSideWire: "14th Wire", filmSideWire: "13th Wire" },
    { min: 152.4, max: 203.2, sourceSideWire: "16th Wire", filmSideWire: "14th Wire" },
    { min: 203.2, max: 254.0, sourceSideWire: "17th Wire", filmSideWire: "16th Wire" },
    { min: 254.0, max: 304.8, sourceSideWire: "18th Wire", filmSideWire: "17th Wire" },
    { min: 304.8, max: 406.4, sourceSideWire: "20th Wire", filmSideWire: "18th Wire" },
    { min: 406.4, max: 508.0, sourceSideWire: "21st Wire", filmSideWire: "20th Wire" }
  ];

  // NB options
  const nbOptions = [6, 8, 10, 15, 20, 25, 32, 40, 50, 65, 80, 100, 125, 150, 200, 250, 300, 350, 400, 450, 500, 600, 750, 900, 1050, 1200];

  // Required Wire options
  const requiredWireOptions = Array.from({ length: 21 }, (_, i) => `${i + 1}${i === 0 ? 'st' : i === 1 ? 'nd' : i === 2 ? 'rd' : 'th'} Wire Visible`);


  //Helper function to parse values with / OR +
  const parseValues = (value) => {
    if (!value || value.trim() === '') return [];
    const valueStr = value.toString().trim();

    // Check for slash (/) OR plus (+)
    if (valueStr.includes('/')) {
      return valueStr.split('/').map(p => parseFloat(p.trim())).filter(n => !isNaN(n));
    }

    if (valueStr.includes('+')) {
      return valueStr.split('+').map(p => parseFloat(p.trim())).filter(n => !isNaN(n));
    }

    const parsed = parseFloat(valueStr);
    return isNaN(parsed) ? [] : [parsed];
  };


  // Helper function to calculate OFD
  const calculateOFD = (nbArray, materialThickness, reinforcement) => {
    if (nbArray && nbArray.length > 0) {
      const ofdValues = nbArray
        .map(nb => nbValues[nb])
        .filter(val => val !== undefined)
        .sort((a, b) => a - b);
      if (ofdValues.length > 0) {
        return ofdValues.join('/');
      }
    }
    const thicknessValues = parseValues(materialThickness);
    const reinforcementValues = parseValues(reinforcement);
    if (thicknessValues.length === 0) return '';
    if (reinforcementValues.length === 0) {
      return thicknessValues.join('/');
    }
    if (reinforcementValues.length === 1) {
      const reinforceValue = reinforcementValues[0];
      const ofdValues = thicknessValues.map(t => (t + reinforceValue).toFixed(2));
      return ofdValues.join('/');
    }
    const ofdValues = [];
    const maxLength = Math.max(thicknessValues.length, reinforcementValues.length);
    for (let i = 0; i < maxLength; i++) {
      const thickness = thicknessValues[i] || thicknessValues[thicknessValues.length - 1] || 0;
      const reinforce = reinforcementValues[i] || reinforcementValues[reinforcementValues.length - 1] || 0;
      ofdValues.push((thickness + reinforce).toFixed(2));
    }
    return ofdValues.join('/');
  };

  // Generate financial years
  const generateFinancialYears = () => {
    const years = [];
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    let startYear = currentMonth >= 4 ? currentYear : currentYear - 1;
    for (let year = 2020; year <= currentYear + 1; year++) {
      years.push(`${year}-${year + 1}`);
    }
    const defaultFY = `${startYear}-${startYear + 1}`;
    return { years, defaultFY };
  };

  const { years: financialYears, defaultFY } = generateFinancialYears();

  // Get yesterday's date
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  // Helper function to get required wire for a specific thickness
  const getRequiredWireForThickness = (totalThickness, iqiPlacement) => {
    const range = wireSelectionRanges.find(r =>
      totalThickness >= r.min && totalThickness < r.max
    );
    if (!range) return null;
    return iqiPlacement === 'Source Side' ? range.sourceSideWire : range.filmSideWire;
  };


  //Main function to calculate required sensitivity
  const calculateRequiredSensitivity = (materialThickness, reinforcement, iqiPlacement) => {
    if (!materialThickness || !iqiPlacement) {
      return '';
    }

    const thicknessValues = parseValues(materialThickness);
    const reinforcementValues = parseValues(reinforcement);

    if (thicknessValues.length === 0) {
      return '';
    }

    //Use Array instead of Set to preserve duplicates
    const requiredWires = [];

    thicknessValues.forEach((thickness, index) => {
      const reinforceValue = reinforcementValues.length === 1
        ? reinforcementValues[0]
        : (reinforcementValues[index] || reinforcementValues[reinforcementValues.length - 1] || 0);

      const totalThickness = thickness + reinforceValue;
      const wire = getRequiredWireForThickness(totalThickness, iqiPlacement);

      if (wire) {
        //Push to array (allows duplicates)
        requiredWires.push(wire);
        console.log(`For thickness ${totalThickness}mm with ${iqiPlacement}: Selected ${wire}`);
      }
    });

    //Join array directly (preserves duplicates and order)
    return requiredWires.length > 0 ? requiredWires.join('/') : '6th Wire';
  };

  // Helper function to format date display
  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Filter film sizes based on search
  const getFilteredFilmSizes = (searchTerm) => {
    if (!searchTerm) return filmSizes;
    return filmSizes.filter(size =>
      `${size.film_size}-${size.film_brand}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  // Add this helper function to calculate No. of Exp
  const calculateNoOfExp = () => {
    if (formData.exposureTechnique === 'Panoramic') {
      const filmsWithJointId = filmDetails.filter(film =>
        film.jointId && film.jointId.trim() !== ''
      ).length;
      return filmsWithJointId.toString();
    } else {
      return filmDetails.length.toString();
    }
  };


  // Form state
  const [formData, setFormData] = useState({
    customer: '',
    customerClient: '',
    financialYear: defaultFY,
    reportDate: new Date().toISOString().split('T')[0],
    backdatedReport: 'no',
    backDate: '',
    backdatedReportNumber: '',
    jobJointId: '',
    reportType: 'Welding',
    materialThickness: '',
    reinforcement: '',
    materialType: 'Steel',
    materialSpecification: '',
    procedureNumber: 'PNS/NDE/RT/01 Rev.3',
    acceptanceStandard: '',
    source: 'Ir 192',
    sourceSize: 'D 2.7 X 1.8 H:mm',
    rtSpecification: 'ASME Sec-V',
    sourceCurie: '15',
    nb: [],
    sfd: '',
    ofd: '',
    sod: '',
    rtPercent: '100%',
    exposureTechnique: '',
    noOfExp: '',
    welderNumber: '',
    screenFB: '0.1 mm (F & B)',
    weldingProcess: '',
    iqiPlacedOn: 'Source Side',
    viewing: 'Single Wall',
    filmBrand: '',
    wireTypeIQI: '',
    filmsCassettes: '1',
    holeTypeIQI: '---',
    requiredSensitivity: '',
    numberOfWallsViewed: '1',
    exposureTime: '',
    requiredWire: [],
    locationMarkedOn: 'Source Side',
    operator: '',
    workDate: getYesterdayDate(),
    tagNo: 'N/A',
    drawingNo: 'N/A',
    ssnNo: 'N/A',
    projectNo: 'N/A',
    soNo: 'N/A',
    poNo: 'N/A',
    msnNo: 'N/A',
    ocNo: 'N/A',
    otherOne: 'ASME',
    otherTwo: '---',
    otherThree: 'N/A',
    otherFour: 'N/A',
    reportNumber: '',
    status: 'Unbilled',
    totalCustomCopies: 0
  });

  // USEEFFECT HOOKS
  // Load copied report data from sessionStorage
  useEffect(() => {
    if (!isEditMode) {
      const copiedData = sessionStorage.getItem('copiedCustomReportData');
      if (copiedData) {
        try {
          const parsedData = JSON.parse(copiedData);
          setFormData(prev => ({
            ...prev,
            ...parsedData,
            workDate: prev.workDate
          }));
          if (parsedData.filmDetails && parsedData.filmDetails.length > 0) {
            setFilmDetails(parsedData.filmDetails);
          }
          sessionStorage.removeItem('copiedCustomReportData');
          setSuccess('Custom Report data copied successfully! Please review and save.');
          setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
          console.error('Error loading copied data:', err);
        }
      }
    }
  }, [isEditMode]);

  // Auto-fill Procedure Number based on selected customer (ONLY for new reports)
  useEffect(() => {
    const fetchProcedureNumber = async () => {
      if (!isEditMode && formData.customer && (!formData.procedureNumber || formData.procedureNumber === 'PNS/NDE/RT/01 Rev.3')) {
        try {
          const token = Cookies.get('token');
          const response = await axios.get(
            `https://app-pune-ndt.cloud/api/customers/${formData.customer}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (response.data.success) {
            const procedureNo = response.data.data.fax || 'PNS/NDE/RT/01 Rev.3';
            setFormData(prev => ({
              ...prev,
              procedureNumber: procedureNo
            }));
          }
        } catch (err) {
          console.error('Error fetching procedure number:', err);
          setFormData(prev => ({
            ...prev,
            procedureNumber: 'PNS/NDE/RT/01 Rev.3'
          }));
        }
      }
    };
    fetchProcedureNumber();
  }, [formData.customer, isEditMode]);

  // Fetch initial data
  useEffect(() => {
    if (isEditMode) {
      fetchReportData();
    }
    fetchCustomers();
    fetchFilmBrands();
    fetchEmployees();
    fetchFilmSizes();
    fetchObservations();
  }, [id]);

  // Auto-update Required Sensitivity
  useEffect(() => {
    const newRequiredSensitivity = calculateRequiredSensitivity(
      formData.materialThickness,
      formData.reinforcement,
      formData.iqiPlacedOn
    );
    setFormData(prev => ({
      ...prev,
      requiredSensitivity: newRequiredSensitivity
    }));
  }, [formData.materialThickness, formData.reinforcement, formData.iqiPlacedOn]);

  // Auto-populate Film Size fields
  useEffect(() => {
    if (filmDetails.length > 0) {
      const lastFilm = filmDetails[filmDetails.length - 1];
      if (lastFilm.filmSize) {
        setAddFilmData(prev => ({
          ...prev,
          filmSize: lastFilm.filmSize
        }));
        setReplaceData(prev => ({
          ...prev,
          fromSize: lastFilm.filmSize
        }));
      }
    }
  }, [filmDetails.length]);

  // Fetch exposure time with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchExposureTime();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [
    formData.sourceCurie,
    formData.materialThickness,
    formData.reinforcement,
    formData.sod,
    formData.filmBrand,
    formData.materialType
  ]);

  // Auto-update IQI Placed On
  useEffect(() => {
    if (formData.exposureTechnique === 'DWSI') {
      setFormData(prev => ({ ...prev, iqiPlacedOn: 'Film Side' }));
    } else if (formData.exposureTechnique) {
      setFormData(prev => ({ ...prev, iqiPlacedOn: 'Source Side' }));
    }
  }, [formData.exposureTechnique]);

  // Auto-update Viewing
  useEffect(() => {
    const doubleWallTechniques = ['DWDI', 'Superimpose', 'DWDI/Superimpose'];
    if (doubleWallTechniques.includes(formData.exposureTechnique)) {
      setFormData(prev => ({ ...prev, viewing: 'Double Wall' }));
    } else if (formData.exposureTechnique) {
      setFormData(prev => ({ ...prev, viewing: 'Single Wall' }));
    }
  }, [formData.exposureTechnique]);

  // Auto-calculate No. of Exp
  useEffect(() => {
    const newNoOfExp = calculateNoOfExp();
    setFormData(prev => ({
      ...prev,
      noOfExp: newNoOfExp
    }));
  }, [filmDetails, formData.exposureTechnique]);

  // Auto-update density based on source
  useEffect(() => {
    if (formData.source === "Ir 192") {
      setFormData(prev => ({ ...prev, density: "2 To 4" }));
    } else if (formData.source === "X ray") {
      setFormData(prev => ({ ...prev, density: "1.8 To 4" }));
    }
  }, [formData.source]);



  // Fetch report data for editing
  const fetchReportData = async () => {
    setFetchingReport(true);
    try {
      const token = Cookies.get('token');
      const response = await axios.get(`https://app-pune-ndt.cloud/api/custom-reports/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        const reportData = response.data.data;
        const formatDateForInput = (dateString) => {
          if (!dateString) return '';
          const date = new Date(dateString);
          const offset = date.getTimezoneOffset();
          const adjustedDate = new Date(date.getTime() - (offset * 60 * 1000));
          return adjustedDate.toISOString().split('T')[0];
        };
        setFormData({
          customer: reportData.customer || '',
          customerClient: reportData.customer_client || '',
          financialYear: reportData.financial_year || defaultFY,
          reportDate: formatDateForInput(reportData.report_date),
          backdatedReport: reportData.backdated_report || 'no',
          backDate: formatDateForInput(reportData.back_date),
          backdatedReportNumber: reportData.backdated_report_number || '',
          jobJointId: reportData.job_joint_id || '',
          reportType: reportData.report_type || 'Welding',
          materialThickness: reportData.material_thickness || '',
          reinforcement: reportData.reinforcement || '',
          materialType: reportData.material_type || 'Steel',
          materialSpecification: reportData.material_specification || '',
          procedureNumber: reportData.procedure_number || '',
          acceptanceStandard: reportData.acceptance_standard || '',
          source: reportData.source || 'Ir 192',
          sourceSize: reportData.source_size || '',
          rtSpecification: reportData.rt_specification || '',
          sourceCurie: reportData.source_curie || '',
          nb: reportData.nb || [],
          sfd: reportData.sfd || '',
          ofd: reportData.ofd || '',
          sod: reportData.sod || '',
          rtPercent: reportData.rt_percent || '',
          exposureTechnique: reportData.exposure_technique || '',
          noOfExp: reportData.no_of_exp || '',
          welderNumber: reportData.welder_number || '',
          screenFB: reportData.screen_fb || '',
          weldingProcess: reportData.welding_process || '',
          iqiPlacedOn: reportData.iqi_placed_on || '',
          viewing: reportData.viewing || '',
          filmBrand: reportData.film_brand || '',
          wireTypeIQI: reportData.wire_type_iqi || '',
          density: reportData.density || '',
          filmsCassettes: reportData.films_cassettes || '',
          holeTypeIQI: reportData.hole_type_iqi || '',
          requiredSensitivity: reportData.required_sensitivity || '',
          numberOfWallsViewed: reportData.number_of_walls_viewed || '',
          exposureTime: reportData.exposure_time || '',
          requiredWire: reportData.required_wire || [],
          locationMarkedOn: reportData.location_marked_on || '',
          operator: reportData.operator || '',
          workDate: formatDateForInput(reportData.work_date),
          tagNo: reportData.tag_no || '',
          drawingNo: reportData.drawing_no || '',
          ssnNo: reportData.ssn_no || '',
          projectNo: reportData.project_no || '',
          soNo: reportData.so_no || '',
          poNo: reportData.po_no || '',
          msnNo: reportData.msn_no || '',
          ocNo: reportData.oc_no || '',
          otherOne: reportData.other_one || '',
          otherTwo: reportData.other_two || '',
          otherThree: reportData.other_three || '',
          otherFour: reportData.other_four || '',
          reportNumber: reportData.report_number || '',
          status: reportData.status || 'Unbilled',
          totalCustomCopies: reportData.total_custom_copies || 0
        });
        if (reportData.filmDetails && reportData.filmDetails.length > 0) {
          const films = reportData.filmDetails.map((film, index) => ({
            id: film.id || index + 1,
            filmSize: film.film_size_display || '',
            jointId: film.joint_id || '',
            sequence: film.sequence || '',
            observation: film.observation_text || '',
            remark: film.remark || 'Acceptable'
          }));
          setFilmDetails(films);
        }
      }
    } catch (err) {
      setError('Failed to fetch custom report data');
      console.error('Error fetching report:', err);
    } finally {
      setFetchingReport(false);
    }
  };

  const fetchFilmSizes = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/film-sizes/all/list', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setFilmSizes(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching film sizes:', err);
    }
  };

  const fetchObservations = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/observations/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Observations loaded:', response.data.data.length);
      if (response.data.success) {
        setObservations(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching observations:', err);
    }
  };

  const fetchCustomers = async () => {
    try {
      const token = Cookies.get('token');
      // const response = await axios.get('https://app-pune-ndt.cloud/api/customers', {
      const response = await axios.get('https://app-pune-ndt.cloud/api/customers/all', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomers(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchFilmBrands = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/film-brands', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setFilmBrands(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching film brands:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = Cookies.get('token');
      const response = await axios.get('https://app-pune-ndt.cloud/api/employees', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchExposureTime = async () => {
    const {
      sourceCurie,
      materialThickness,
      reinforcement,
      sod,
      filmBrand,
      materialType
    } = formData;
    if (!sourceCurie || !materialThickness || !sod || !filmBrand || !materialType) {
      return;
    }
    try {
      const token = Cookies.get('token');
      const response = await axios.post(
        'https://app-pune-ndt.cloud/api/calculate-exposure-time',
        {
          sourceCurie,
          materialThickness,
          reinforcement: reinforcement || '0',
          sod,
          filmBrand,
          materialType
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      if (response.data.success) {
        setFormData(prev => ({
          ...prev,
          exposureTime: response.data.exposureTime
        }));
      }
    } catch (error) {
      console.error('Error calculating exposure time:', error);
    }
  };

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...formData, [name]: value };
    if (name === 'source') {
      updatedData.sourceSize = value === 'Ir 192' ? 'D 2.7 X 1.8 H:mm' : '3 x 3';
    }
    if (name === 'materialThickness' || name === 'reinforcement') {
      const newOfd = calculateOFD(
        updatedData.nb,
        name === 'materialThickness' ? value : updatedData.materialThickness,
        name === 'reinforcement' ? value : updatedData.reinforcement
      );
      updatedData.ofd = newOfd;
    }
    setFormData(updatedData);
  };

  // Handle NB checkbox changes
  const handleNBChange = (value) => {
    setFormData(prev => {
      const newNb = prev.nb.includes(value)
        ? prev.nb.filter(item => item !== value)
        : [...prev.nb, value];
      const newOfd = calculateOFD(newNb, prev.materialThickness, prev.reinforcement);
      return {
        ...prev,
        nb: newNb,
        ofd: newOfd
      };
    });
  };

  // Handle Required Wire checkbox changes
  const handleRequiredWireChange = (value) => {
    setFormData(prev => ({
      ...prev,
      requiredWire: prev.requiredWire.includes(value)
        ? prev.requiredWire.filter(item => item !== value)
        : [...prev.requiredWire, value]
    }));
  };

  //Film detail change handler with useCallback
  const handleFilmDetailChange = useCallback((id, field, value) => {
    setFilmDetails(prev => prev.map(film => {
      if (film.id === id) {
        let updatedFilm = { ...film, [field]: value };
        if (field === 'observation' && value) {
          const matchingObs = observations.find(
            obs => obs.observation_text.toLowerCase() === value.toLowerCase()
          );
          if (matchingObs) {
            updatedFilm.remark = matchingObs.remark_option;
          }
        }
        return updatedFilm;
      }
      return film;
    }));
  }, [observations]);

  //Add film detail with useCallback
  const handleAddFilmDetail = useCallback((afterId) => {
    setFilmDetails(prev => {
      const clickedIndex = prev.findIndex(f => f.id === afterId);
      const clickedFilm = prev[clickedIndex];
      const newId = prev.length > 0 ? Math.max(...prev.map(f => f.id)) + 1 : 1;
      const newFilm = {
        id: newId,
        filmSize: clickedFilm.filmSize,
        jointId: "",
        sequence: "",
        observation: "NSI",
        remark: "Acceptable"
      };
      const newFilmDetails = [...prev];
      newFilmDetails.splice(clickedIndex + 1, 0, newFilm);
      return newFilmDetails;
    });
  }, []);

  //Remove film detail with useCallback
  const handleRemoveFilmDetail = useCallback((id) => {
    setFilmDetails(prev => prev.filter(film => film.id !== id));
  }, []);

  const handleAddFilms = () => {
    const quantity = parseInt(addFilmData.quantity) || 1;
    const groups = parseInt(addFilmData.groups) || 1;
    const selectedFilmSize = addFilmData.filmSize;
    const isCs = addFilmData.isCs;
    if (!selectedFilmSize) {
      alert('Please select a film size');
      return;
    }
    const filmSizeExists = filmSizes.some(size =>
      `${size.film_size}-${size.film_brand}` === selectedFilmSize
    );
    if (!filmSizeExists) {
      alert(`Film size "${selectedFilmSize}" not found in system. Please select a valid film size.`);
      return;
    }
    const newFilms = [];
    const startId = filmDetails.length > 0 ? Math.max(...filmDetails.map(f => f.id)) + 1 : 1;
    const getAlphabetWithNumber = (index) => {
      const cycle = Math.floor(index / 26);
      const letter = String.fromCharCode(65 + (index % 26));
      return cycle === 0 ? letter : `${letter}${cycle}`;
    };
    const generateSequence = (index, type, totalInGroup) => {
      const positionInGroup = index % totalInGroup;
      if (type === 'Single Alphabet') {
        return getAlphabetWithNumber(positionInGroup);
      } else if (type === 'Double Alphabet') {
        const first = getAlphabetWithNumber(positionInGroup);
        let secondIndex = positionInGroup + 1;
        if (isCs && secondIndex === totalInGroup) {
          secondIndex = 0;
        }
        const second = getAlphabetWithNumber(secondIndex);
        return `${first}-${second}`;
      } else if (type === 'Single Number') {
        return (positionInGroup + 1).toString();
      } else if (type === 'Double Number') {
        let secondNum = positionInGroup + 1;
        if (isCs && secondNum === totalInGroup) {
          secondNum = 0;
        }
        return `${positionInGroup}-${secondNum}`;
      }
      return '';
    };
    const totalFilms = quantity * groups;
    for (let i = 0; i < totalFilms; i++) {
      newFilms.push({
        id: startId + i,
        filmSize: selectedFilmSize,
        jointId: "",
        sequence: generateSequence(i, addFilmData.sequenceType, quantity),
        observation: "NSI",
        remark: "Acceptable"
      });
    }
    setFilmDetails(prev => [...prev, ...newFilms]);
    setAddFilmData(prev => ({
      ...prev,
      filmSize: selectedFilmSize,
      sequenceType: 'Single Alphabet',
      quantity: '1',
      groups: '1',
      isCs: false
    }));
    setReplaceData(prev => ({
      ...prev,
      fromSize: selectedFilmSize
    }));
  };

  const handleReplace = async () => {
    const { fromSize, toSize, findJointId, replaceJointId } = replaceData;
    let replacedFilmSize = false;
    let replacedJointId = false;
    let filmSizeCount = 0;
    let jointIdCount = 0;
    if (fromSize && toSize) {
      if (fromSize === toSize) {
        alert('Cannot replace film size with the same value');
        return;
      }
      const toSizeExists = filmSizes.some(size =>
        `${size.film_size}-${size.film_brand}` === toSize
      );
      if (!toSizeExists) {
        alert(`Film size "${toSize}" not found in system. Please select a valid film size.`);
        return;
      }
      filmDetails.forEach(film => {
        if (film.filmSize === fromSize) {
          filmSizeCount++;
        }
      });
      if (filmSizeCount > 0) {
        replacedFilmSize = true;
      } else {
        alert(`Film size "${fromSize}" not found in current films.`);
        return;
      }
    }
    if (findJointId && replaceJointId) {
      if (findJointId === replaceJointId) {
        alert('Find and replace text for Joint ID cannot be the same');
        return;
      }
      filmDetails.forEach(film => {
        if (film.jointId && film.jointId.includes(findJointId)) {
          jointIdCount++;
        }
      });
      if (jointIdCount > 0) {
        replacedJointId = true;
      }
    }
    if (!replacedFilmSize && !replacedJointId) {
      alert('Please fill in fields to replace Film Size or Joint ID');
      return;
    }
    let confirmMessage = 'This will:\n';
    if (replacedFilmSize) {
      confirmMessage += `- Replace film size "${fromSize}" with "${toSize}" in ${filmSizeCount} film(s)\n`;
    }
    if (replacedJointId) {
      confirmMessage += `- Replace "${findJointId}" with "${replaceJointId}" in ${jointIdCount} Joint ID(s)\n`;
    }
    confirmMessage += '\nContinue?';
    if (!window.confirm(confirmMessage)) {
      return;
    }
    setFilmDetails(prev => prev.map(film => {
      let updatedFilm = { ...film };
      if (replacedFilmSize && film.filmSize === fromSize) {
        updatedFilm.filmSize = toSize;
      }
      if (replacedJointId && film.jointId) {
        updatedFilm.jointId = film.jointId.replaceAll(findJointId, replaceJointId);
      }
      return updatedFilm;
    }));
    let successMessage = 'Successfully replaced:\n';
    if (replacedFilmSize) {
      successMessage += `- Film size in ${filmSizeCount} film(s)\n`;
      setAddFilmData(prev => ({
        ...prev,
        filmSize: toSize
      }));
    }
    if (replacedJointId) {
      successMessage += `- Joint ID text in ${jointIdCount} film(s)\n`;
    }
    alert(successMessage);
    setReplaceData({
      fromSize: replacedFilmSize ? toSize : fromSize,
      toSize: '',
      findJointId: '',
      replaceJointId: ''
    });
  };

  // Form submission with validation
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (filmDetails.length === 0) {
      setError('Minimum one film is required to create or update a custom report!');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const filmsWithoutSequence = filmDetails.filter(
      film => !film.sequence || film.sequence.trim() === ''
    );
    if (filmsWithoutSequence.length > 0) {
      setError(`Sequence is required for all films! ${filmsWithoutSequence.length} film(s) missing sequence.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setLoading(true);
    try {
      const token = Cookies.get('token');
      if (isEditMode) {
        const response = await axios.put(
          `https://app-pune-ndt.cloud/api/custom-reports/${id}`,
          {
            ...formData,
            filmDetails: filmDetails
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.data.success) {
          setSuccess('Custom Report updated successfully!');
          setTimeout(() => {
            navigate('/custom-reports');
          }, 2000);
        }
      } else {
        const response = await axios.post(
          'https://app-pune-ndt.cloud/api/custom-reports',
          {
            ...formData,
            filmDetails: filmDetails
          },
          {
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        if (response.data.success) {
          setSuccess(`Custom Report created successfully! Report Number: ${response.data.data.reportNumber}`);
          setTimeout(() => {
            navigate('/custom-reports');
          }, 2000);
        }
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError(err.response?.data?.message || `❌ Failed to ${isEditMode ? 'update' : 'create'} custom report`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  if (fetchingReport) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Loading custom report...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[1600px] mx-auto">

        {/* Combined Header Card */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-2">
          <div className="bg-gradient-to-r from-indigo-900 to-indigo-800 px-6 py-4">
            <h1 className="text-3xl font-bold text-white">
              {isEditMode ? 'Edit Custom Report' : 'Create New Custom Report'}
            </h1>
          </div>
          <div className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="space-y-2">
                  {(isEditMode || formData.reportNumber) && (
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-medium text-gray-900 min-w-[120px]">Report Number:</span>
                      <span className="text-lg font-semibold text-indigo-900">{formData.reportNumber}</span>
                    </div>
                  )}
                  {formData.jobJointId && (
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-medium text-gray-900 min-w-[120px]">Job/Joint ID:</span>
                      <span className="text-lg font-semibold text-indigo-900">{formData.jobJointId}</span>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => navigate('/custom-reports')}
                className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
                title="Back to Custom Reports"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
            {success}
          </div>
        )}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-4">

            {(isEditMode || formData.reportNumber) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 bg-gray-10 p-4 rounded-lg border border-indigo-200">
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-1">
                    Report Number {!isEditMode && '(You can edit)'}
                  </label>
                  <input
                    type="text"
                    name="reportNumber"
                    value={formData.reportNumber}
                    onChange={handleChange}
                    className="w-full px-3 py-2 text-sm border border-indigo-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter report number or leave blank for auto-generation"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {isEditMode
                      ? 'You can edit the report number'
                      : 'Edit or leave blank for auto-generation'}
                  </p>
                </div>
                {formData.backdatedReport === 'yes' && formData.backdatedReportNumber && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">Backdated Report Number</label>
                    <input
                      type="text"
                      value={formData.backdatedReportNumber}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-indigo-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                )}
                {formData.backdatedReport === 'yes' && formData.backDate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-900 mb-1">Backdated Report Date</label>
                    <input
                      type="text"
                      value={formatDateDisplay(formData.backDate)}
                      readOnly
                      className="w-full px-3 py-2 text-sm border border-indigo-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Row 1 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                <SearchableSelect
                  options={customers}
                  value={formData.customer}
                  onChange={(id) => setFormData(prev => ({ ...prev, customer: id }))}
                  placeholder="Select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Customer Client</label>
                <SearchableSelect
                  options={customers}
                  value={formData.customerClient}
                  onChange={(id) => setFormData(prev => ({ ...prev, customerClient: id }))}
                  placeholder="Select"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Financial Year *</label>
                <select name="financialYear" required value={formData.financialYear} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  {financialYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Date *</label>
                <input type="date" name="reportDate" required value={formData.reportDate} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Back Dated Report *</label>
                <select name="backdatedReport" required value={formData.backdatedReport} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </div>
            </div>

            {/* Conditional Row */}
            {formData.backdatedReport === 'yes' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Back Date *</label>
                  <input type="date" name="backDate" required value={formData.backDate} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Backdated Report Number *</label>
                  <input type="text" name="backdatedReportNumber" required value={formData.backdatedReportNumber} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter number" />
                </div>
              </div>
            )}

            {/* Row 2 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Job/Joint ID *</label>
                <input type="text" name="jobJointId" required value={formData.jobJointId} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter Job ID" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Report Type *</label>
                <select name="reportType" required value={formData.reportType} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="Welding">Welding</option>
                  <option value="Casting">Casting</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Thickness *</label>
                <input type="text" name="materialThickness" required value={formData.materialThickness} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="mm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reinforcement *</label>
                <input type="text" name="reinforcement" required value={formData.reinforcement} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="mm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Type *</label>
                <select name="materialType" required value={formData.materialType} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="Steel">Steel</option>
                  <option value="Aluminium">Aluminium</option>
                  <option value="Iron">Iron</option>
                </select>
              </div>
            </div>

            {/* Row 3 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Material Specification *</label>
                <input type="text" name="materialSpecification" required value={formData.materialSpecification} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter spec" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Procedure Number *</label>
                <input
                  type="text"
                  name="procedureNumber"
                  required
                  value={formData.procedureNumber}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50 font-semibold text-blue-900"
                  placeholder="Auto-filled from customer"
                  title="Auto-filled based on selected customer"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Acceptance Standard *</label>
                <input type="text" name="acceptanceStandard" required value={formData.acceptanceStandard} onChange={handleChange} list="acceptanceStandardOptions" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="ASME/IBR/EN" />
                <datalist id="acceptanceStandardOptions">
                  <option value="ASME SEC VIII Div 1 UW51" />
                  <option value="ASME SEC VIII Div 1 UW52" />
                  <option value="ASME B31.3" />
                  <option value="IBR 1950" />
                  <option value="EN" />
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source *</label>
                <select name="source" required value={formData.source} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="Ir 192">Ir 192</option>
                  <option value="X ray">X ray</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Size</label>
                <input type="text" name="sourceSize" value={formData.sourceSize} readOnly className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed" />
              </div>
            </div>

            {/* Row 4 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RT Specification *</label>
                <input
                  type="text"
                  name="rtSpecification"
                  required
                  value={formData.rtSpecification}
                  onChange={handleChange}
                  list="rt-specifications"
                  placeholder="Type or select from suggestions..."
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <datalist id="rt-specifications">
                  <option value="ASME Sec-V" />
                  <option value="IBR 1950" />
                </datalist>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source Curie *</label>
                <input type="text" name="sourceCurie" required value={formData.sourceCurie} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              {/* NB Dropdown */}
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">NB</label>
                <button
                  type="button"
                  onClick={() => setNbDropdownOpen(!nbDropdownOpen)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-left flex justify-between items-center"
                >
                  <span className="text-gray-700 truncate">
                    {formData.nb.length === 0
                      ? 'Select NB'
                      : `${formData.nb.length} selected: ${formData.nb.sort((a, b) => a - b).join(', ')}`}
                  </span>
                  <svg
                    className={`w-4 h-4 transition-transform flex-shrink-0 ml-2 ${nbDropdownOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {nbDropdownOpen && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2">
                      {nbOptions.map((nb) => (
                        <label
                          key={nb}
                          className="flex items-center px-3 py-2 hover:bg-blue-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.nb.includes(nb)}
                            onChange={() => handleNBChange(nb)}
                            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <span className="ml-3 text-sm text-gray-700">{nb}</span>
                        </label>
                      ))}
                    </div>
                    <div className="border-t border-gray-200 p-2 flex justify-between">
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, nb: nbOptions }))}
                        className="text-xs text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, nb: [] }))}
                        className="text-xs text-red-600 hover:text-red-800"
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SFD *</label>
                <input
                  type="text"
                  name="sfd"
                  required
                  value={formData.sfd}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter SFD"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OFD *</label>
                <input
                  type="text"
                  name="ofd"
                  required
                  value={formData.ofd}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter OFD"
                />
              </div>
            </div>

            {/* Row 5 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SOD *</label>
                <input
                  type="text"
                  name="sod"
                  required
                  value={formData.sod}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter SOD"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RT Percent *</label>
                <input
                  type="text"
                  name="rtPercent"
                  required
                  value={formData.rtPercent}
                  onChange={handleChange}
                  list="rtPercentOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <datalist id="rtPercentOptions">
                  <option value="5%" />
                  <option value="10%" />
                  <option value="25%" />
                  <option value="100%" />
                  <option value="Spot" />
                  <option value="Full" />
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IQI Selection</label>
                <input
                  type="text"
                  name="otherOne"
                  value={formData.otherOne}
                  onChange={handleChange}
                  list="iqiOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Select or enter standard"
                />
                <datalist id="iqiOptions">
                  <option value="ASME" />
                  <option value="EN" />
                  <option value="BS" />
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exposure Technique *</label>
                <select
                  name="exposureTechnique"
                  required
                  value={formData.exposureTechnique}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select</option>
                  <option value="SWSI">SWSI</option>
                  <option value="DWSI">DWSI</option>
                  <option value="Panoramic">Panoramic</option>
                  <option value="Superimpose">Superimpose</option>
                  <option value="DWDI">DWDI</option>
                  <option value="DWDI/Superimpose">DWDI/Superimpose</option>
                  <option value="SWSI/Panoramic">SWSI/Panoramic</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. of Exp *</label>
                <input
                  type="text"
                  name="noOfExp"
                  required
                  value={formData.noOfExp}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Auto-calculated"
                />
              </div>
            </div>



            {/* Row 6 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Welder Number *</label>
                <input type="text" name="welderNumber" required value={formData.welderNumber} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="Enter number" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Screen F & B *</label>
                <input type="text" name="screenFB" required value={formData.screenFB} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Welding Process *</label>
                <input
                  type="text"
                  name="weldingProcess"
                  required
                  value={formData.weldingProcess}
                  onChange={handleChange}
                  list="weldingProcessOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter or select process"
                />
                <datalist id="weldingProcessOptions">
                  <option value="GTAW" />
                  <option value="SMAW" />
                  <option value="FCAW" />
                  <option value="GTAW+SMAW" />
                  <option value="GTAW+FCAW" />
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">IQI Placed On *</label>
                <select name="iqiPlacedOn" required value={formData.iqiPlacedOn} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="Film Side">Film Side</option>
                  <option value="Source Side">Source Side</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Viewing *</label>
                <input type="text" name="viewing" required value={formData.viewing} onChange={handleChange} list="viewingOptions" className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <datalist id="viewingOptions">
                  <option value="Single Wall" />
                  <option value="Double Wall" />
                </datalist>
              </div>
            </div>

            {/* Row 7 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Film Brand *</label>
                <select name="filmBrand" required value={formData.filmBrand} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Select</option>
                  {filmBrands.map((brand) => (
                    <option key={brand.id} value={brand.id}>{brand.actual_film_brand_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Wire Type IQI *</label>
                <input
                  type="text"
                  name="wireTypeIQI"
                  required
                  value={formData.wireTypeIQI}
                  onChange={handleChange}
                  list="wireTypeOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Select Wire Type"
                />
                <datalist id="wireTypeOptions">
                  {formData.otherOne === "ASME" && (
                    <>
                      {[
                        "ASTM 1A-06", "ASTM 1B-11", "ASTM 1C-16", "ASTM 1D-21",
                        "ASTM 01A-06", "ASTM 01B-11", "ASTM 01C-16", "ASTM 01D-21",
                        "ASTM 02A-06", "ASTM 02B-11", "ASTM 02C-16", "ASTM 02D-21",
                        "ASTM 3A-06", "ASTM 3B-11", "ASTM 3C-16", "ASTM 3D-21",
                        "ASTM 4A-06", "ASTM 4B-11", "ASTM 4C-16", "ASTM 4D-21"
                      ].map(val => (
                        <option key={val} value={formData.exposureTechnique === "DWSI" ? `${val} (F)` : val} />
                      ))}
                    </>
                  )}
                  {formData.otherOne === "EN" && (
                    <>
                      {[
                        "1 FE EN", "6 FE EN", "10 FE EN", "13 FE EN",
                        "1 AL EN", "6 AL EN", "10 AL EN", "13 AL EN",
                        "1 CU EN", "6 CU EN", "10 CU EN", "13 CU EN",
                        "1 TI EN", "6 TI EN", "10 TI EN", "13 TI EN"
                      ].map(val => (
                        <option key={val} value={formData.exposureTechnique === "DWSI" ? `${val} (F)` : val} />
                      ))}
                    </>
                  )}
                  {formData.otherOne === "BS" && (
                    <>
                      {["1 ISO 7", "6 ISO 12", "10 ISO 16", "13 ISO 19"].map(val => (
                        <option key={val} value={formData.exposureTechnique === "DWSI" ? `${val} (F)` : val} />
                      ))}
                    </>
                  )}
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Density *</label>
                <input
                  type="text"
                  name="density"
                  required
                  value={formData.density}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Select source to auto-fill"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Films/Cassettes *</label>
                <input type="text" name="filmsCassettes" required value={formData.filmsCassettes} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hole Type IQI *</label>
                <input
                  type="text"
                  name="holeTypeIQI"
                  required
                  value={formData.holeTypeIQI}
                  onChange={handleChange}
                  list="holeTypeOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={formData.exposureTechnique === "DWSI" ? "Select (F) Hole Type" : "Select Hole Type"}
                />
                <datalist id="holeTypeOptions">
                  {[
                    "ASTM 10", "ASTM 12", "ASTM 15", "ASTM 17", "ASTM 20",
                    "ASTM 25", "ASTM 30", "ASTM 35", "ASTM 40", "ASTM 50",
                    "ASTM 60", "ASTM 80"
                  ].map((val) => (
                    <option
                      key={val}
                      value={formData.exposureTechnique === "DWSI" ? `${val} (F)` : val}
                    />
                  ))}
                </datalist>
              </div>
            </div>

            {/* Row 8 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Essential Hole</label>
                <input
                  type="text"
                  name="otherTwo"
                  value={formData.otherTwo}
                  onChange={handleChange}
                  list="essentialHoleOptions"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Optional (e.g., 2T)"
                />
                <datalist id="essentialHoleOptions">
                  <option value="2T" />
                  <option value="---" />
                </datalist>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Essential Wire *</label>
                <input
                  type="text"
                  name="requiredSensitivity"
                  required
                  value={formData.requiredSensitivity}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Auto-calculated"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Walls Viewed *</label>
                <input type="text" name="numberOfWallsViewed" required value={formData.numberOfWallsViewed} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Exposure Time *</label>
                <input
                  type="text"
                  name="exposureTime"
                  required
                  value={formData.exposureTime}
                  onChange={handleChange}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-blue-50 font-semibold text-blue-900"
                  placeholder="Auto-calculated"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Marked On *</label>
                <select name="locationMarkedOn" required value={formData.locationMarkedOn} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="Source Side">Source Side</option>
                  <option value="Film Side">Film Side</option>
                </select>
              </div>
            </div>

            {/* Row 9 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Operator *</label>
                <select name="operator" required value={formData.operator} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  <option value="">Select</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Date *</label>
                <input type="date" name="workDate" required value={formData.workDate} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tag No *</label>
                <input type="text" name="tagNo" required value={formData.tagNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Drawing No *</label>
                <input type="text" name="drawingNo" required value={formData.drawingNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SSN No *</label>
                <input type="text" name="ssnNo" required value={formData.ssnNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            {/* Row 10 */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project No *</label>
                <input type="text" name="projectNo" required value={formData.projectNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SO No *</label>
                <input type="text" name="soNo" required value={formData.soNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">PO No *</label>
                <input type="text" name="poNo" required value={formData.poNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MSN No *</label>
                <input type="text" name="msnNo" required value={formData.msnNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OC No *</label>
                <input type="text" name="ocNo" required value={formData.ocNo} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            {/* Row 11 - Additional Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OA No *</label>
                <input type="text" name="otherThree" required value={formData.otherThree} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Item No *</label>
                <input type="text" name="otherFour" required value={formData.otherFour} onChange={handleChange} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
            </div>

            {/* Total Films */}
            <div className="mt-4 border-t pt-4 text-left">
              <span className="text-lg font-bold text-gray-900">
                Total Films: {filmDetails.length}
              </span>
            </div>

            {/* Horizontal Line */}
            <div className="border-t border-gray-300 mt-4 mb-6"></div>

            {/* Film Sizes Summary */}
            <div className="mt-4 bg-gray-10 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start">
                <span className="text-base font-semibold text-gray-800 mr-3">Film Sizes:</span>
                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {(() => {
                    const filmSizeCounts = {};
                    filmDetails.forEach(film => {
                      if (film.filmSize) {
                        filmSizeCounts[film.filmSize] = (filmSizeCounts[film.filmSize] || 0) + 1;
                      }
                    });

                    const filmSizeEntries = Object.entries(filmSizeCounts);

                    if (filmSizeEntries.length === 0) {
                      return <span className="text-gray-500 italic">No film sizes selected</span>;
                    }

                    return filmSizeEntries.map(([filmSize, count], index) => (
                      <span key={filmSize} className="text-base text-gray-800">
                        <span className="font-semibold text-gray-900">{index + 1})</span>{' '}
                        <span className="font-medium">{filmSize}</span>{' '}
                        <span className="text-gray-800">=</span>{' '}
                        <span className="font-bold text-indigo-700">{count}</span>
                        {index < filmSizeEntries.length - 1 && ''}
                      </span>
                    ));
                  })()}
                </div>
              </div>
            </div>

            {/* Horizontal Line */}
            <div className="border-t border-gray-300 mt-4 mb-6"></div>

            {/* Remarks Summary */}
            <div className="mt-4 bg-gray-10 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-start">
                <span className="text-base font-semibold text-gray-800 mr-3">Remarks:</span>
                <div className="flex flex-wrap gap-x-6 gap-y-2">
                  {(() => {
                    const remarkCounts = {};
                    filmDetails.forEach(film => {
                      if (film.remark) {
                        remarkCounts[film.remark] = (remarkCounts[film.remark] || 0) + 1;
                      }
                    });

                    const displayRemarks = ['Reshoot', 'Repair', 'Retake', 'Acceptable'];
                    const filteredEntries = displayRemarks
                      .map(remark => [remark, remarkCounts[remark] || 0])
                      .filter(([remark, count]) => count > 0);

                    if (filteredEntries.length === 0) {
                      return <span className="text-gray-900 italic">No remarks (Reshoot/Repair/Retake/Acceptable)</span>;
                    }

                    return filteredEntries.map(([remark, count]) => (
                      <span key={remark} className="text-base text-gray-900">
                        <span className="font-medium text-gray-800">{remark}</span>
                        <span className="text-gray-500 mx-1">=</span>
                        <span className="font-bold text-red-700">{count}</span>
                      </span>
                    ));
                  })()}
                </div>
              </div>
            </div>



            {/* Film Details Section */}
            <div className="mt-8 border-t pt-6">
              <div className="bg-indigo-900 text-white px-4 py-3 rounded-t-lg">
                <h2 className="text-lg font-bold">Film Details</h2>
              </div>

              {/* Film Details Table */}
              <div className="bg-gray-50 p-4 rounded-b-lg">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse bg-white">
                    <thead>
                      <tr className="bg-gray-900 text-white">
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400 w-12">#</th>
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400">Film Size</th>
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400">Joint ID</th>
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400">
                          Sequence <span className="text-red-400">*</span>
                        </th>
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400">Observation</th>
                        <th className="px-3 py-2 text-left text-sm font-medium border border-gray-400">Remark</th>
                        <th className="px-3 py-2 text-center text-sm font-medium border border-gray-400 w-24">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filmDetails.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-3 py-8 text-center text-gray-500">
                            No films added yet. Use "ADD FILMS" button below to add films.
                          </td>
                        </tr>
                      ) : (
                        filmDetails.map((film, index) => (
                          <FilmDetailRow
                            key={film.id}
                            film={film}
                            index={index}
                            onUpdate={handleFilmDetailChange}
                            onRemove={handleRemoveFilmDetail}
                            onAdd={handleAddFilmDetail}
                            filmSizes={filmSizes}
                            observations={observations}
                            remarkOptions={remarkOptions}
                          />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Horizontal Line - Top */}
                <div className="border-t border-gray-300 mt-6 mb-4"></div>

                {/* Add Films Row */}
                <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Film Size *</label>
                    <input
                      type="text"
                      list="filmsize-add"
                      value={addFilmData.filmSize}
                      onChange={(e) => setAddFilmData(prev => ({ ...prev, filmSize: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Search or select film size..."
                    />
                    <datalist id="filmsize-add">
                      {filmSizes.map((size) => (
                        <option key={size.id} value={`${size.film_size}-${size.film_brand}`}>
                          {size.film_size}-{size.film_brand}
                        </option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Sequence Type *</label>
                    <select
                      value={addFilmData.sequenceType}
                      onChange={(e) => setAddFilmData(prev => ({ ...prev, sequenceType: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option>Single Alphabet</option>
                      <option>Double Alphabet</option>
                      <option>Single Number</option>
                      <option>Double Number</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Films *</label>
                    <input
                      type="number"
                      min="1"
                      value={addFilmData.quantity}
                      onChange={(e) => setAddFilmData(prev => ({ ...prev, quantity: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Groups *</label>
                    <input
                      type="number"
                      min="1"
                      value={addFilmData.groups}
                      onChange={(e) => setAddFilmData(prev => ({ ...prev, groups: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">ISCS</label>
                    <div className="flex items-center h-[38px]">
                      <input
                        type="checkbox"
                        checked={addFilmData.isCs}
                        onChange={(e) => setAddFilmData(prev => ({ ...prev, isCs: e.target.checked }))}
                        className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
                    <button
                      type="button"
                      onClick={handleAddFilms}
                      className="bg-indigo-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition w-full h-[38px]"
                    >
                      ADD FILMS
                    </button>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">&nbsp;</label>
                    <div className="flex items-center h-[38px]">
                      <span className="text-base font-bold text-gray-900 whitespace-nowrap">Total Films: {filmDetails.length}</span>
                    </div>
                  </div>
                </div>

                {/* Horizontal Line - Bottom */}
                <div className="border-t border-gray-300 mt-4 mb-6"></div>

                {/* Replace Section */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Select Film Size to Replace</label>
                    <input
                      type="text"
                      list="filmsize-replace-from"
                      value={replaceData.fromSize}
                      onChange={(e) => setReplaceData(prev => ({ ...prev, fromSize: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Search or select..."
                    />
                    <datalist id="filmsize-replace-from">
                      {filmSizes.map((size) => (
                        <option key={size.id} value={`${size.film_size}-${size.film_brand}`}>
                          {size.film_size}-{size.film_brand}
                        </option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Replace Film Size With</label>
                    <input
                      type="text"
                      list="filmsize-replace-to"
                      value={replaceData.toSize}
                      onChange={(e) => setReplaceData(prev => ({ ...prev, toSize: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Search or select..."
                    />
                    <datalist id="filmsize-replace-to">
                      {filmSizes.map((size) => (
                        <option key={size.id} value={`${size.film_size}-${size.film_brand}`}>
                          {size.film_size}-{size.film_brand}
                        </option>
                      ))}
                    </datalist>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Find Joint ID Text</label>
                    <input
                      type="text"
                      value={replaceData.findJointId}
                      onChange={(e) => setReplaceData(prev => ({ ...prev, findJointId: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Type"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Replace Joint ID With</label>
                    <input
                      type="text"
                      value={replaceData.replaceJointId}
                      onChange={(e) => setReplaceData(prev => ({ ...prev, replaceJointId: e.target.value }))}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Type"
                    />
                  </div>

                  <div>
                    <button
                      type="button"
                      onClick={handleReplace}
                      className="bg-indigo-900 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition w-full h-[38px]"
                    >
                      REPLACE
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Horizontal Line - Bottom */}
            <div className="border-t border-gray-300 mt-4 mb-6"></div>

            {/* Action Buttons */}
            <div className="space-y-4 pt-4">
              {/* Circular Loading Spinner */}
              {loading && (
                <div className="flex items-center justify-center py-4 space-x-3">
                  <svg
                    className="w-6 h-6 animate-spin text-indigo-600"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-gray-700">
                    Generating Custom Report...
                  </p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="flex items-center justify-center py-4 space-x-3 bg-green-50 border border-green-200 rounded-lg">
                  <svg
                    className="w-6 h-6 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-green-700">
                    {success}
                  </p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="flex items-center justify-center py-4 space-x-3 bg-red-50 border border-red-200 rounded-lg">
                  <svg
                    className="w-6 h-6 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <p className="text-sm font-semibold text-red-700">
                    {error}
                  </p>
                </div>
              )}

              {/* Submit and Cancel Buttons */}
              <div className="flex space-x-4">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-gradient-to-r from-indigo-900 to-indigo-900 text-white px-6 py-3 rounded-lg font-medium hover:from-indigo-800 hover:to-indigo-900 transition duration-300 disabled:opacity-50 shadow-lg"
                >
                  {isEditMode ? 'Update Custom Report' : 'Create Custom Report'}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/custom-reports')}
                  disabled={loading}
                  className="px-6 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
              </div>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default NewCustomReport;